import { useState, useEffect, useCallback } from 'react';
import './style.scss';
import { TranslationService } from '../../services/translation.service';
import TabList from '../Shared/TabList';
import TabListTabContent from '../Shared/TabList/components/TabListTabContent';
import TabListNavItem from '../Shared/TabList/components/TabListNavItem';
import SearchTabPanel from './components/SearchTabPanel';
import type { IFindSearchResults } from './models/find-search-results.model';
import SearchAutocomplete, { type ISearchAutocompleteState } from '../Shared/SearchAutocomplete';

enum SearchPageTabType {
  unified = 'unified',
  faq = 'faq',
}

function SearchPage() {
  const [autocompleteState, setAutompleteState] = useState<ISearchAutocompleteState>({ value: { searchText: '', selectedItem: undefined, preventSearch: true } });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTabType, setSelectedTabType] = useState(SearchPageTabType.unified);
  const [numberOfUnifiedResults, setNumberOfUnifiedResults] = useState(-1);
  const [numberOfFaqResults, setNumberOfFaqResults] = useState(-1);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    setSearchQuery(searchParams.get('q') ?? '');
    setAutompleteState({ value: { searchText: searchParams.get('q') ?? '', selectedItem: undefined, preventSearch: true } });
    const tabType = searchParams.get('t') === 'faq' ? SearchPageTabType.faq : SearchPageTabType.unified;
    setSelectedTabType(tabType);
  }, []);

  function updateSearchParams(tabType: SearchPageTabType | undefined = undefined, query: string | undefined = undefined) {
    const searchParams = new URLSearchParams(window.location.search);
    if (tabType) searchParams.set('t', tabType);
    if (query) searchParams.set('q', query);
    const newUrl = `${window.location.origin}${window.location.pathname}?${searchParams.toString()}`;
    window.history.pushState({ path: newUrl }, '', newUrl);
  }

  function handleOnSubmit() {
    updateSearchParams(undefined, autocompleteState.value.searchText);
    setSearchQuery(autocompleteState.value.searchText);
  }

  function handleOnUnifiedActivated() {
    if (selectedTabType === SearchPageTabType.unified) return;
    setSelectedTabType(SearchPageTabType.unified);
    updateSearchParams(SearchPageTabType.unified, undefined);
  }

  function handleOnFaqActivated() {
    if (selectedTabType === SearchPageTabType.faq) return;
    setSelectedTabType(SearchPageTabType.faq);
    updateSearchParams(SearchPageTabType.faq, undefined);
  }

  function handleOnSearchTextChange(newSearchText: string) {
    setAutompleteState({ value: { searchText: newSearchText, selectedItem: undefined, preventSearch: false } });
  }

  function handleTabListSelectedChange(index: number) {
    if (index === 0) handleOnUnifiedActivated();
    else if (index === 1) handleOnFaqActivated();
  }

  const handleOnSearchCompleted = useCallback((numberOfUnified: number, numberOfFaq: number) => {
    setNumberOfUnifiedResults(numberOfUnified);
    setNumberOfFaqResults(numberOfFaq);
  }, []);

  const handleOnSearchQueryChanged = useCallback((newSearchQuery: string) => {
    setSearchQuery(newSearchQuery);
    updateSearchParams(undefined, newSearchQuery);
  }, []);

  return (
    <div>
      <form role="search" className="mb-4" onSubmit={(e) => e.preventDefault()}>
        <div className="d-flex">
          <SearchAutocomplete
            id="search-page-autocomplete"
            labelText={TranslationService.get('common.search')}
            placeholderText={TranslationService.get('common.search')}
            className="w-100"
            state={autocompleteState}
            onSelect={(item) => {
              if (item) {
                setSearchQuery(item.query);
                updateSearchParams(undefined, item.query);
                setAutompleteState({ value: { searchText: item.query, selectedItem: item, preventSearch: false } });
              }
            }}
            onSearchTextChange={handleOnSearchTextChange}
          />
          <button type="submit" className="btn btn-primary py-0 ms-2" onClick={handleOnSubmit}>
            {TranslationService.get('common.search')}
          </button>
        </div>
      </form>

      <TabList
        id="search-page"
        activeIndex={selectedTabType === SearchPageTabType.unified ? 0 : 1}
        onSelectedChange={handleTabListSelectedChange}
        navItems={[
          <TabListNavItem key={'nav-item-0'} onActivate={handleOnUnifiedActivated}>
            {TranslationService.get('searchpage.searchresults')} {numberOfUnifiedResults > -1 && <span className="badge text-bg-light ms-1">{numberOfUnifiedResults}</span>}
          </TabListNavItem>,
          <TabListNavItem key={'nav-item-1'} onActivate={handleOnFaqActivated}>
            {TranslationService.get('searchpage.commonquestions')} {numberOfFaqResults > -1 && <span className="badge text-bg-light ms-1">{numberOfFaqResults}</span>}
          </TabListNavItem>,
        ]}
        tabContents={[
          <TabListTabContent key={'tab-item-0'}>
            <>
              <h2 className="visually-hidden">{TranslationService.get('searchpage.searchresults')}</h2>
              <SearchTabPanel
                isActive={selectedTabType === SearchPageTabType.unified}
                query={searchQuery}
                searchEndpoint="unified"
                onSearchCompleted={handleOnSearchCompleted}
                onSearchQueryChanged={handleOnSearchQueryChanged}
                numberOfResultsFunc={useCallback((data: IFindSearchResults) => data.numberOfUnifiedResults, [])}
              />
            </>
          </TabListTabContent>,
          <TabListTabContent key={'tab-item-1'}>
            <>
              <h2 className="visually-hidden">{TranslationService.get('searchpage.commonquestions')}</h2>
              <SearchTabPanel
                isActive={selectedTabType === SearchPageTabType.faq}
                query={searchQuery}
                searchEndpoint="faq"
                onSearchCompleted={handleOnSearchCompleted}
                onSearchQueryChanged={handleOnSearchQueryChanged}
                numberOfResultsFunc={useCallback((data: IFindSearchResults) => data.numberOfFaqResults, [])}
              />
            </>
          </TabListTabContent>,
        ]}
      ></TabList>
    </div>
  );
}

export default SearchPage;
